import {makeAutoObservable, observable} from 'mobx'
import {Lang} from 'yougile-lang'
import {logger, Util} from 'yougile-util'
import {
  CompanyLinkDataResp,
  DataObjectId,
  GenResp,
  KeyReq,
  KeyResp
} from 'yougile-model'
import {Backend} from '../../../common/backend'
import {addNotAcceptedToSearchParams} from '../../client/add-not-accepted-to-search-params'
import {Refresh} from '../../client/refresh'
import {Company as CompanyCtl} from '../../extensions/company'
import {PageService} from '../../extensions/page-service'
import {MessageBox} from '../message-box'
import {Company} from './types'

export const errCannotAddUserByLicense = 'Cannot add user by license'

export type PageType = 'loading' | 'login' | 'main'
export type Screen =
  | 'enter'
  | 'register'
  | 'main'
  | 'sent-email'
  | 'register-finish'

interface State {
  screen: Screen
  wait: boolean
  error: string
  company?: Company
  regKey?: string
}

export class CompanyLoginStore {
  state: State

  constructor() {
    this.state = {screen: 'main', wait: false, error: ''}
    makeAutoObservable(this, {state: observable.shallow}, {autoBind: true})

    this.state.regKey = (location.search.split('reg-key=')[1] || '').split(
      '&'
    )[0]
  }

  setCompany(company: Company): void {
    this.state.company = company
    this.state.wait = false
  }
  setScreen(screen: Screen): void {
    this.state.screen = screen
    this.state.error = ''
  }
  setScreenEnter(): void {
    this.setScreen('enter')
  }
  setScreenRegister(): void {
    this.setScreen('register')
  }
  setError(error: string): void {
    this.state.error = errorResolver(error)
  }
  setWait(wait: boolean): void {
    this.state.wait = wait
  }
  enterCurrentCompany(): void {
    const companyId = this.state.company?.id
    if (companyId) {
      void this.enterCurrent(companyId)
    }
  }

  async loader(): Promise<Company> {
    const link = PageService.getCompanySubdomain()
    if (this.state.regKey) {
      const resp = await Backend.postJSON(
        '/data/company-link-register-approve',
        {link, key: this.state.regKey}
      ).catch(e => ({error: errorResolver(e + ''), result: 'error'}))
      if (resp.error) {
        MessageBox.show(errorResolver(resp.error), 'danger')
        this.state.regKey = undefined
        return this.loader()
      } else {
        const {companyName, companyId, email} = resp as any
        return {
          id: companyId,
          name: companyName,
          fetchError: '',
          regEmail: email
        }
      }
    }

    let err = ''
    const res = await Backend.getJSON<CompanyLinkDataResp>(
      '/data/company-link-info',
      {data: {link, ...Backend.loadCred()}}
    ).catch(e => {
      err = e
      return {result: 'error', error: ''} as CompanyLinkDataResp
    })
    if (err || res.error) {
      return {
        fetchError: err || res.error,
        name: undefined,
        id: undefined
      } as unknown as Company
    }
    if (!res.companyId) {
      return {
        fetchError: 'not found',
        name: undefined,
        id: undefined
      } as unknown as Company
    }
    return {
      fetchError: '',
      id: res.companyId,
      name: res.companyName,
      user: res.user
    }
  }

  async enterCurrent(
    companyId: DataObjectId,
    isFinish = true
  ): Promise<string> {
    const link = PageService.getCompanySubdomain()
    try {
      const res = await Backend.postJSON<{companyStatus?: 'pending'} & GenResp>(
        '/data/company-link-enter',
        {
          ...Backend.loadCred(),
          companyId,
          link
        }
      )

      if (res.error) {
        MessageBox.show(errorResolver(res.error), 'danger')
        return res.error
      }

      if (res.companyStatus !== 'pending') {
        CompanyCtl.saveSelectedCompanyId(companyId)
        PageService.enterSubdomain()
        return ''
      }

      if (isFinish) {
        companyLoginStore.setScreen('register-finish')
      } else {
        location.search = addNotAcceptedToSearchParams()
        void Refresh.clearCredentials(false)
      }

      return ''
    } catch (e) {
      logger.error(e)
      if (e instanceof Error) {
        MessageBox.show(errorResolver(e.message), 'danger')
        return '  ' // hide err message to always showed
      }
      return 'Error'
    }
  }

  async login(email: string, password: string): Promise<{error?: string}> {
    if (!Util.isEmail(email)) {
      return {error: Lang.msg.emailIncorrect}
    }
    if (!password || password.length < 3) {
      return {error: Lang.msg.incorrectEmailPass}
    }
    const keyReq: KeyReq = {email, password}

    try {
      const res = await Backend.postJSON<KeyResp>('/data/key', keyReq)

      if (!res.key || !res.userId) {
        return {error: ''}
      }

      const companyId = this.state.company?.id
      if (!companyId) {
        return {error: ''}
      }

      Backend.saveCredentials(res.userId, res.key)

      const errEnterCurrent = await this.enterCurrent(companyId, false)
      if (errEnterCurrent) {
        return {error: errEnterCurrent}
      }

      PageService.enterSubdomain()
      return {error: ''}
    } catch (err) {
      const msg =
        typeof err === 'string' ||
        (err instanceof Error && err.message.toLowerCase().includes('password'))
          ? Lang.msg.incorrectEmailPass
          : Lang.msg.noConnectionShort

      return {error: msg}
    }
  }

  async register(
    email: string,
    username: string,
    password: string
  ): Promise<{error?: string}> {
    if (!Util.isEmail(email)) {
      return {error: Lang.msg.emailIncorrect}
    }
    if (!password || password.length < 3) {
      return {error: Lang.msg.noPassword}
    }
    if (!username || username.length < 3) {
      return {error: Lang.msg.nameShort}
    }

    const link = PageService.getCompanySubdomain()
    const error = await Backend.postJSON(
      '/data/company-link-register-request',
      {
        email,
        username,
        password,
        link
      }
    )
      .then(res => errorResolver(res.error) || '')
      .catch(e => e + '')
    return {error}
  }
}

export const companyLoginStore = new CompanyLoginStore()

export const errorResolver = (err?: string): string => {
  if (err?.includes(errCannotAddUserByLicense)) {
    return Lang.msg.noLicense.limitIsExceeded
  }

  return err || ''
}
